import { untilDestroyed, UntilDestroy } from '@ngneat/until-destroy';
import { Location } from '@angular/common';
import { Injectable } from '@angular/core';
import {
  Observable,
  of,
  take,
  concatMap,
  catchError,
  map,
  takeWhile,
  tap,
} from 'rxjs';
import { MatDialog } from '@angular/material/dialog';

import { CurrentUserDataService, RoutingService } from '.';
import { UserDetailsFacade } from '@shared/state/facades';
import { PersistenceService } from './persistence';
import { SelectedSubproject, UserSession } from '@shared/models';
import { Rights } from '@shared/enums';
import { ActivateComponentService } from './activate-component.service';
import {
  canDeactivateUnsavedChanges,
  ignoreNextGuardCall$,
} from '@shared/guards';

@UntilDestroy()
@Injectable()
export class SelectedAccountService {
  constructor(
    private userDetailsFacade: UserDetailsFacade,
    private persistenceService: PersistenceService,
    private currentUserDataService: CurrentUserDataService,
    private routingService: RoutingService,
    private location: Location,
    private activateComponentService: ActivateComponentService,
    private dialog: MatDialog
  ) {}

  selectClient(clientId: string, forceSelect = false): Observable<UserSession> {
    let previousSelectedClientId: string;

    return this.activateComponentService.activatedComponent.pipe(
      untilDestroyed(this),
      take(1),
      concatMap((activatedComponent) => {
        if (!activatedComponent) {
          return of(true);
        }

        return canDeactivateUnsavedChanges(
          activatedComponent,
          this.dialog
        ).pipe(tap((result) => result && ignoreNextGuardCall$.next(true)));
      }),
      takeWhile((result) => result),
      concatMap(() => this.userDetailsFacade.query.userDetails$.pipe(take(1))),
      concatMap((userDetails) => {
        previousSelectedClientId = userDetails?.selectedClientId;

        if (clientId === userDetails?.selectedClientId && !forceSelect) {
          return of(userDetails);
        }

        // This has to be updated first because it is used in the HttpInterceptor
        this.userDetailsFacade.setSelectedClientId(clientId);

        return this.persistenceService.updateSelectedClient(clientId);
      }),
      concatMap((updatedUserDetails) => {
        this.userDetailsFacade.setUserSession(updatedUserDetails);

        const subprojectId =
          updatedUserDetails?.selectedSubprojectId ??
          updatedUserDetails?.subprojects[0]?.id;

        if (!subprojectId) {
          return of(updatedUserDetails);
        }

        return this.currentUserDataService
          .setupSelectedSubproject(subprojectId, updatedUserDetails)
          .pipe(take(1));
      }),
      concatMap((userSession) => {
        const subprojectId =
          userSession?.selectedSubprojectId ?? userSession?.subprojects[0]?.id;

        const isErrorPage = this.location.path().includes('error');

        const selectedSubProject = userSession.subprojects.find(
          (s) => s.id === subprojectId
        );

        return this.routingService
          .goToFirstAllowedRoute(
            userSession,
            subprojectId,
            !isErrorPage ? this.location.path() : null,
            !isErrorPage ? this.hasAccessRights(selectedSubProject) : true
          )
          .pipe(map(() => userSession));
      }),
      catchError(() => {
        if (previousSelectedClientId) {
          this.userDetailsFacade.setSelectedClientId(previousSelectedClientId);
        }
        ignoreNextGuardCall$.next(false);
        return of(null);
      })
    );
  }

  //#region Private Methods

  private hasAccessRights(selectedSubproject: SelectedSubproject): boolean {
    return (
      (this.location.path().includes('/dprs/') &&
        selectedSubproject?.dprScreen !== Rights.None) ||
      (this.location.path().includes('/project-setup/') &&
        selectedSubproject?.projectSetup !== Rights.None)
    );
  }

  //#endregion
}
